import React, { useState, useEffect } from "react";
import "./Promotions.css";
import ContactSection from "../components/Contact/contact_homepage";

// ✅ Use dynamic API base URL
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://threegor-1.onrender.com";

// --- API FUNCTION ---
const fetchPromotions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/promotions`);
    if (!response.ok) {
      throw new Error("Failed to fetch promotions");
    }
    const data = await response.json();
    return Array.isArray(data) ? data : data.data || [];
  } catch (error) {
    console.error("Error fetching promotions:", error);
    throw error;
  }
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState({});
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Image Error Handler
  const handleImageError = (e) => {
    e.target.onError = null;
    e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found";
    e.target.style.filter = "grayscale(100%)";
  };

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPromotions();
      setPromotions(data || []);
    } catch (error) {
      console.error("Error loading promotions:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle card image navigation
  const handleNextImage = (promoId, e) => {
    e.stopPropagation();
    const promo = promotions.find(p => p._id === promoId || p.id === promoId);
    if (promo && promo.images) {
      const currentIndex = selectedImageIndex[promoId] || 0;
      const nextIndex = (currentIndex + 1) % promo.images.length;
      setSelectedImageIndex({
        ...selectedImageIndex,
        [promoId]: nextIndex
      });
    }
  };

  const handlePrevImage = (promoId, e) => {
    e.stopPropagation();
    const promo = promotions.find(p => p._id === promoId || p.id === promoId);
    if (promo && promo.images) {
      const currentIndex = selectedImageIndex[promoId] || 0;
      const prevIndex = currentIndex === 0 ? promo.images.length - 1 : currentIndex - 1;
      setSelectedImageIndex({
        ...selectedImageIndex,
        [promoId]: prevIndex
      });
    }
  };

  // Open modal with promotion details
  const openModal = (promotion) => {
    setSelectedPromotion(promotion);
    setModalImageIndex(0);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedPromotion(null);
    setModalImageIndex(0);
    document.body.style.overflow = 'auto';
  };

  // Navigate modal images
  const handleModalImageChange = (index) => {
    setModalImageIndex(index);
  };

  const handleModalPrevImage = () => {
    if (selectedPromotion && selectedPromotion.images) {
      const prevIndex = modalImageIndex === 0 ? selectedPromotion.images.length - 1 : modalImageIndex - 1;
      setModalImageIndex(prevIndex);
    }
  };

  const handleModalNextImage = () => {
    if (selectedPromotion && selectedPromotion.images) {
      const nextIndex = (modalImageIndex + 1) % selectedPromotion.images.length;
      setModalImageIndex(nextIndex);
    }
  };

  // Calculate discount percentage
  const calculateDiscount = (original, current) => {
    if (!original || !current) return null;
    const discount = ((original - current) / original * 100).toFixed(0);
    return discount > 0 ? discount : null;
  };

  if (loading) {
    return (
      <div className="promotions-page">
        <div className="promotions-header">
          <h1>โปรโมชั่นพิเศษ</h1>
          <p>รับส่วนลดสุดพิเศษสำหรับงานตกแต่งของคุณ</p>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>กำลังโหลดโปรโมชั่น...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="promotions-page">
        <div className="promotions-header">
          <h1>โปรโมชั่นพิเศษ</h1>
          <p>รับส่วนลดสุดพิเศษสำหรับงานตกแต่งของคุณ</p>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <p>เกิดข้อผิดพลาด: {error}</p>
          <button onClick={loadPromotions} className="retry-button">
            ลองอีกครั้ง
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="promotions-page">
      {/* Header Section */}
      <div className="promotions-header">
        <h1>โปรโมชั่นพิเศษ</h1>
        <p>รับส่วนลดสุดพิเศษสำหรับงานตกแต่งของคุณ</p>
      </div>

      {/* Promotions Grid */}
      {promotions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎁</div>
          <p>ไม่มีโปรโมชั่นในขณะนี้</p>
        </div>
      ) : (
        <div className="promotions-grid">
          {promotions.map((promo) => {
            const currentImageIndex = selectedImageIndex[promo._id || promo.id] || 0;
            const displayImages = promo.images ? promo.images.slice(0, 5) : [];
            const currentImage = displayImages[currentImageIndex] || promo.image;
            const discount = calculateDiscount(promo.originalPrice, promo.price);

            return (
              <div 
                key={promo._id || promo.id} 
                className="promo-card"
                onClick={() => openModal(promo)}
              >
                {/* Sale Badge */}
                {discount && (
                  <div className="sale-badge">
                    ลด {discount}%
                  </div>
                )}

                {/* Image Gallery */}
                <div className="promo-image-gallery">
                  {currentImage && (
                    <img 
                      src={currentImage} 
                      alt={promo.title}
                      onError={handleImageError}
                    />
                  )}

                  {/* Navigation Arrows */}
                  {displayImages.length > 1 && (
                    <>
                      <button 
                        className="gallery-nav prev-btn"
                        onClick={(e) => handlePrevImage(promo._id || promo.id, e)}
                      >
                        ❮
                      </button>
                      <button 
                        className="gallery-nav next-btn"
                        onClick={(e) => handleNextImage(promo._id || promo.id, e)}
                      >
                        ❯
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  {displayImages.length > 1 && (
                    <div className="image-count">
                      {currentImageIndex + 1} / {displayImages.length}
                    </div>
                  )}

                  {/* Price Badge */}
                  <div className="promo-price-badge">
                    <div className="price-main">฿{promo.price?.toLocaleString() || 'N/A'}</div>
                    {promo.originalPrice && (
                      <div className="price-original">฿{promo.originalPrice.toLocaleString()}</div>
                    )}
                  </div>
                </div>

                {/* Thumbnails */}
                {displayImages.length > 1 && (
                  <div className="promo-thumbnails">
                    {displayImages.map((img, idx) => (
                      <img 
                        key={idx}
                        src={img} 
                        alt={`${promo.title} ${idx + 1}`}
                        onError={handleImageError}
                        className={`thumbnail ${idx === currentImageIndex ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImageIndex({
                            ...selectedImageIndex,
                            [promo._id || promo.id]: idx
                          });
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Content */}
                <div className="promo-content">
                  <h3 className="promo-title">{promo.title}</h3>
                  {promo.subtitle && (
                    <p className="promo-subtitle">{promo.subtitle}</p>
                  )}

                  {/* Features */}
                  {promo.features && promo.features.length > 0 && (
                    <ul className="promo-features">
                      {promo.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                      {promo.features.length > 4 && (
                        <li>และอีกมากมาย...</li>
                      )}
                    </ul>
                  )}

                  {/* Actions */}
                  <div className="promo-actions">
                    <button 
                      className="promo-btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(promo);
                      }}
                    >
                      ดูรายละเอียด
                    </button>
                    <button 
                      className="promo-btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open('https://www.facebook.com/messages/t/100064243770130', '_blank');
                      }}
                    >
                      สอบถาม
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal for Promotion Details */}
      {modalOpen && selectedPromotion && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✕</button>
            
            <div className="modal-body">
              {/* Left Side - Images */}
              <div className="modal-images">
                <div className="modal-main-image">
                  {selectedPromotion.images && selectedPromotion.images[modalImageIndex] && (
                    <img 
                      src={selectedPromotion.images[modalImageIndex]} 
                      alt={selectedPromotion.title}
                      onError={handleImageError}
                    />
                  )}
                  
                  {/* Modal Navigation */}
                  {selectedPromotion.images && selectedPromotion.images.length > 1 && (
                    <>
                      <button className="modal-nav prev" onClick={handleModalPrevImage}>
                        ❮
                      </button>
                      <button className="modal-nav next" onClick={handleModalNextImage}>
                        ❯
                      </button>
                    </>
                  )}
                </div>

                {/* Image Counter */}
                {selectedPromotion.images && selectedPromotion.images.length > 1 && (
                  <div className="modal-image-count">
                    รูปที่ {modalImageIndex + 1} จาก {selectedPromotion.images.length}
                  </div>
                )}
                
                {/* Thumbnails */}
                {selectedPromotion.images && selectedPromotion.images.length > 1 && (
                  <div className="modal-thumbnails">
                    {selectedPromotion.images.map((img, idx) => (
                      <img 
                        key={idx}
                        src={img} 
                        alt={`${selectedPromotion.title} ${idx + 1}`}
                        onError={handleImageError}
                        className={idx === modalImageIndex ? 'active' : ''}
                        onClick={() => handleModalImageChange(idx)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Right Side - Details */}
              <div className="modal-details">
                <h2 className="modal-title">{selectedPromotion.title}</h2>
                
                {selectedPromotion.subtitle && (
                  <p className="promo-subtitle">{selectedPromotion.subtitle}</p>
                )}

                {/* Price Section */}
                <div className="modal-price-section">
                  <div className="modal-current-price">
                    ฿{selectedPromotion.price?.toLocaleString() || 'N/A'}
                  </div>
                  {selectedPromotion.originalPrice && (
                    <div className="modal-original-price">
                      ฿{selectedPromotion.originalPrice.toLocaleString()}
                    </div>
                  )}
                  {calculateDiscount(selectedPromotion.originalPrice, selectedPromotion.price) && (
                    <div className="modal-discount">
                      ประหยัด {calculateDiscount(selectedPromotion.originalPrice, selectedPromotion.price)}%
                    </div>
                  )}
                </div>

                {/* Features */}
                {selectedPromotion.features && selectedPromotion.features.length > 0 && (
                  <div className="modal-section">
                    <h3>รายละเอียดสินค้า</h3>
                    <ul className="modal-features-list">
                      {selectedPromotion.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Detailed Features */}
                {selectedPromotion.detailedFeatures && selectedPromotion.detailedFeatures.length > 0 && (
                  <div className="modal-section">
                    <h3>ข้อมูลเพิ่มเติม</h3>
                    <ul className="modal-detailed-features">
                      {selectedPromotion.detailedFeatures.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Specifications */}
                {(selectedPromotion.dimensions || selectedPromotion.material || 
                  selectedPromotion.warranty || selectedPromotion.installation) && (
                  <div className="modal-section">
                    <h3>วัสดุ</h3>
                    <div className="modal-specs">
                      {selectedPromotion.dimensions && (
                        <div className="spec-item">
                          <span className="spec-label">ขนาด:</span>
                          <span className="spec-value">{selectedPromotion.dimensions}</span>
                        </div>
                      )}
                      {selectedPromotion.material && (
                        <div className="spec-item">
                          <span className="spec-label">วัสดุ:</span>
                          <span className="spec-value">{selectedPromotion.material}</span>
                        </div>
                      )}
                      {selectedPromotion.warranty && (
                        <div className="spec-item">
                          <span className="spec-label">การรับประกัน:</span>
                          <span className="spec-value">{selectedPromotion.warranty}</span>
                        </div>
                      )}
                      {selectedPromotion.installation && (
                        <div className="spec-item">
                          <span className="spec-label">การติดตั้ง:</span>
                          <span className="spec-value">{selectedPromotion.installation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="modal-actions">
                  <button 
                    className="modal-btn-primary"
                    onClick={() => window.open('https://www.facebook.com/messages/t/100064243770130', '_blank')}
                  >
                    สอบถามรายละเอียด
                  </button>
                  <button className="modal-btn-secondary" onClick={closeModal}>
                    ปิด
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="promotions-cta">
        <h2>พร้อมเริ่มโครงการกับเราหรือยัง?</h2>
        <p>รับคำปรึกษาฟรี! พร้อมส่วนลดพิเศษสำหรับโครงการของคุณ</p>
        <button 
          className="cta-button"
          onClick={() => window.open('https://www.facebook.com/messages/t/100064243770130', '_blank')} 
        >
          ปรึกษาโครงการ
        </button>
      </div>

      <ContactSection />
    </div>
  );
};

export default Promotions;